// src/views/admin/AdminAccueil.jsx
// (--pr, --pr-lt, --pr-dk, --or, --or-dk, --or-lt, --t, --s2).
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import ADSetupChecklist from "./ADSetupChecklist";

const heureFR = () => {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
};

const dateFR = () =>
  new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });

const initiales = (nom) =>
  (nom || "A").trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join("") || "A";

export default function AdminAccueil({ slug, adminNom }) {
  const navigate = useNavigate();
  const { getEtablissementCourant } = useAdminService();

  const [etablissement, setEtablissement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const heroRef = useRef(null);

  useEffect(() => {
    if (!slug) return;
    getEtablissementCourant(slug)
      .then(setEtablissement)
      .catch(() => setEtablissement(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setMousePosition({ x, y });
    };

    const hero = heroRef.current;
    if (hero) {
      hero.addEventListener('mousemove', handleMouseMove);
      return () => hero.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  if (loading) return <Loader />;

  // Fallback défensif : prop adminNom en priorité, sinon un éventuel champ côté API
  const nomAffiche = adminNom || etablissement?.admin_nom || etablissement?.admin?.nom;

  const anneeLibelle = etablissement?.annee_active?.libelle || etablissement?.annee_active_libelle;
  const nbEleves = etablissement?.nb_eleves ?? etablissement?.stats?.nb_eleves;
  const nbClasses = etablissement?.nb_classes ?? etablissement?.stats?.nb_classes;
  const heure = currentTime.getHours().toString().padStart(2, '0');
  const minutes = currentTime.getMinutes().toString().padStart(2, '0');
  const secondes = currentTime.getSeconds().toString().padStart(2, '0');

  return (
    <div className="ad-accueil-wrapper">
      {/* Checklist de configuration — au-dessus du hero, ne casse pas son layout */}
      <ADSetupChecklist
        slug={slug}
        onNavigateTab={(tab) => navigate(`/${slug}/admin/parametres?tab=${tab}`)}
      />

      <div className="ad-accueil" ref={heroRef}>

        {/* Effet de fond avec suivi de souris */}
        <div
          className="ad-mouse-glow"
          style={{
            left: `${mousePosition.x}%`,
            top: `${mousePosition.y}%`
          }}
        />

        {/* Grille d'arrière-plan */}
        <div className="ad-grid" aria-hidden="true" />

        {/* Particules */}
        <div className="ad-particles" aria-hidden="true">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="ad-particle"
              style={{
                '--delay': `${i * 0.7}s`,
                '--duration': `${15 + Math.random() * 15}s`,
                '--size': `${2 + Math.random() * 6}px`,
                '--x': `${Math.random() * 100}%`,
                '--y': `${Math.random() * 100}%`,
                '--tx': `${(Math.random() - 0.5) * 200}px`,
                '--ty': `${(Math.random() - 0.5) * 200}px`,
              }}
            />
          ))}
        </div>

        <div className="ad-hero">
          {/* Ornements décoratifs */}
          <div className="ad-ornament ad-ornament-1" aria-hidden="true" />
          <div className="ad-ornament ad-ornament-2" aria-hidden="true" />
          <div className="ad-ornament ad-ornament-3" aria-hidden="true" />
          <div className="ad-ornament ad-ornament-4" aria-hidden="true" />

          <div className="ad-hero-content">
            {/* En-tête */}
            <div className="ad-header">
              <div className="ad-greeting">
                <span className="ad-wave">👋</span>
                <span className="ad-heure">{heureFR()}</span>
                {nomAffiche && <span className="ad-nom">, {nomAffiche}</span>}
              </div>
              <div className="ad-header-right">
                <div className="ad-date">{dateFR()}</div>
                <div className="ad-time">
                  <i className="ti ti-clock" />
                  <span className="ad-hours">{heure}</span>
                  <span className="ad-separator">:</span>
                  <span className="ad-minutes">{minutes}</span>
                  <span className="ad-seconds">{secondes}</span>
                </div>
                <button
                  type="button"
                  className="ad-dashboard-btn"
                  onClick={() => navigate(`/${slug}/admin/dashboard`)}
                >
                  <i className="ti ti-layout-dashboard" />
                  Vue d'ensemble
                </button>
              </div>
            </div>

            {/* Corps principal */}
            <div className="ad-main">
              <div className="ad-avatar-wrapper">
                <div className="ad-avatar-ring">
                  <div className="ad-avatar-ring-inner" />
                </div>
                <div className="ad-avatar">
                  {initiales(nomAffiche)}
                  <div className="ad-avatar-status">
                    <span className="ad-status-dot" />
                  </div>
                </div>
              </div>

              <div className="ad-title-wrapper">
                <span className="ad-title-prefix">Bienvenue</span>
                <h1 className="ad-title">
                  {etablissement?.nom || "Votre établissement"}
                </h1>
              </div>

              <div className="ad-badges">
                {anneeLibelle && (
                  <span className="ad-badge ad-badge-primary">
                    <i className="ti ti-calendar-check" />
                    <span>Année {anneeLibelle}</span>
                  </span>
                )}
                <span className="ad-badge ad-badge-success">
                  <i className="ti ti-circle-check" />
                  <span>En ligne</span>
                </span>
                <span className="ad-badge ad-badge-info">
                  <i className="ti ti-shield-check" />
                  <span>Sécurisé</span>
                </span>
              </div>

              <div className="ad-stats">
                {nbEleves != null && (
                  <div className="ad-stat">
                    <div className="ad-stat-icon">
                      <i className="ti ti-users" />
                    </div>
                    <div className="ad-stat-info">
                      <span className="ad-stat-value">
                        {nbEleves.toLocaleString()}
                      </span>
                      <span className="ad-stat-label">Élèves inscrits</span>
                    </div>
                  </div>
                )}
                {nbClasses != null && (
                  <div className="ad-stat">
                    <div className="ad-stat-icon">
                      <i className="ti ti-school" />
                    </div>
                    <div className="ad-stat-info">
                      <span className="ad-stat-value">{nbClasses}</span>
                      <span className="ad-stat-label">Classes actives</span>
                    </div>
                  </div>
                )}
                {nbEleves != null && nbClasses != null && nbClasses > 0 && (
                  <div className="ad-stat">
                    <div className="ad-stat-icon">
                      <i className="ti ti-chart-bar" />
                    </div>
                    <div className="ad-stat-info">
                      <span className="ad-stat-value">
                        {Math.round(nbEleves / nbClasses)}
                      </span>
                      <span className="ad-stat-label">Moyenne/classe</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Message inspirant */}
              <div className="ad-quote">
                <div className="ad-quote-icon">
                  <i className="ti ti-quote" />
                </div>
                <div className="ad-quote-content">
                  <span className="ad-quote-text">
                    "L'éducation est l'arme la plus puissante pour changer le monde"
                  </span>
                  <span className="ad-quote-author">— Nelson Mandela</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .ad-accueil-wrapper {
            display: flex;
            flex-direction: column;
            height: 100%;
          }

          .ad-accueil {
            /* Fond de la page d'accueil : doit rester identique à celui de
               .ad-avatar-ring-inner (anneau autour de l'avatar). */
            --ad-bg: #F7F9FB;

            height: calc(100vh - 80px);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0;
            padding: 0;
            position: relative;
            overflow: hidden;
            background: var(--ad-bg);
          }

          /* === EFFET DE SUIVI SOURIS === */
          .ad-mouse-glow {
            position: absolute;
            width: 600px;
            height: 600px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(63, 100, 127, 0.07) 0%, transparent 70%);
            transform: translate(-50%, -50%);
            pointer-events: none;
            transition: left 0.15s ease-out, top 0.15s ease-out;
            z-index: 0;
          }

          /* === GRILLE === */
          .ad-grid {
            position: absolute;
            inset: 0;
            background-image:
              linear-gradient(rgba(63, 100, 127, 0.04) 1px, transparent 1px),
              linear-gradient(90deg, rgba(63, 100, 127, 0.04) 1px, transparent 1px);
            background-size: 60px 60px;
            pointer-events: none;
            z-index: 0;
            mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
            -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
          }

          /* === PARTICULES === */
          .ad-particles {
            position: absolute;
            inset: 0;
            pointer-events: none;
            z-index: 0;
            overflow: hidden;
          }

          .ad-particle {
            position: absolute;
            border-radius: 50%;
            background: rgba(63, 100, 127, 0.10);
            animation: float-particle var(--duration) ease-in-out infinite;
            animation-delay: var(--delay);
            width: var(--size);
            height: var(--size);
            left: var(--x);
            top: var(--y);
            transform: translate(var(--tx), var(--ty));
          }

          @keyframes float-particle {
            0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.2; }
            25% { transform: translate(30px, -40px) scale(1.3); opacity: 0.8; }
            50% { transform: translate(-30px, 40px) scale(0.7); opacity: 0.4; }
            75% { transform: translate(20px, -20px) scale(1.1); opacity: 0.6; }
          }

          /* === HERO === */
          .ad-hero {
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
          }

          /* Ornements : anneaux bleus et anneaux dorés */
          .ad-ornament {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
            opacity: 0.3;
          }

          .ad-ornament-1 {
            width: 500px;
            height: 500px;
            top: -150px;
            right: -150px;
            border: 1px solid rgba(63, 100, 127, 0.08);
            animation: orbit 20s linear infinite;
          }

          .ad-ornament-2 {
            width: 350px;
            height: 350px;
            bottom: -100px;
            left: -100px;
            border: 1px solid rgba(176, 141, 42, 0.18);
            animation: orbit 25s linear infinite reverse;
          }

          .ad-ornament-3 {
            width: 200px;
            height: 200px;
            top: 10%;
            right: 15%;
            border: 1px solid rgba(63, 100, 127, 0.06);
            animation: pulse-ornament 8s ease-in-out infinite;
          }

          .ad-ornament-4 {
            width: 150px;
            height: 150px;
            bottom: 20%;
            left: 10%;
            border: 1px solid rgba(176, 141, 42, 0.14);
            animation: pulse-ornament 12s ease-in-out infinite reverse;
          }

          @keyframes orbit {
            0% { transform: rotate(0deg) scale(1); }
            50% { transform: rotate(180deg) scale(1.05); }
            100% { transform: rotate(360deg) scale(1); }
          }

          @keyframes pulse-ornament {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.7; }
          }

          .ad-hero-content {
            position: relative;
            z-index: 2;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            max-width: 900px;
            padding: 48px 40px;
            height: 100%;
            max-height: 600px;
          }

          /* === HEADER === */
          .ad-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-bottom: 16px;
            border-bottom: 1px solid rgba(226, 232, 240, 0.5);
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.3);
            padding: 12px 20px;
            border-radius: 16px;
            margin-bottom: 4px;
          }

          .ad-greeting {
            font-size: 15px;
            font-weight: 500;
            color: #64748B;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .ad-wave {
            font-size: 20px;
            animation: wave 2.5s ease-in-out infinite;
            display: inline-block;
          }

          @keyframes wave {
            0%, 100% { transform: rotate(0deg); }
            25% { transform: rotate(20deg); }
            75% { transform: rotate(-10deg); }
          }

          .ad-heure {
            color: var(--t);
            font-weight: 700;
          }

          /* Nom de l'admin : bleu uni (le dégradé texte rendait mal avec l'or) */
          .ad-nom {
            color: var(--pr);
            font-weight: 700;
          }

          .ad-header-right {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .ad-date {
            font-size: 13px;
            color: #94A3B8;
            font-weight: 500;
          }

          .ad-time {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 13px;
            font-weight: 600;
            color: var(--t);
            background: rgba(63, 100, 127, 0.08);
            padding: 4px 14px;
            border-radius: 8px;
            font-variant-numeric: tabular-nums;
          }

          .ad-time i {
            font-size: 14px;
            color: var(--pr);
            margin-right: 4px;
          }

          .ad-dashboard-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 7px 16px;
            background: linear-gradient(135deg, var(--pr), var(--pr-lt));
            color: #fff;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            font-family: inherit;
            cursor: pointer;
            white-space: nowrap;
            box-shadow: 0 3px 10px rgba(63, 100, 127, 0.28);
            transition: all 0.2s ease;
          }

          .ad-dashboard-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 5px 16px rgba(63, 100, 127, 0.38);
          }

          .ad-dashboard-btn i {
            font-size: 15px;
          }

          .ad-hours, .ad-minutes {
            font-weight: 700;
            color: var(--t);
          }

          .ad-seconds {
            font-weight: 400;
            color: #94A3B8;
            font-size: 11px;
          }

          .ad-separator {
            color: #94A3B8;
            animation: blink 1s step-end infinite;
          }

          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }

          /* === MAIN === */
          .ad-main {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            flex: 1;
            gap: 14px;
            padding: 12px 0;
          }

          .ad-avatar-wrapper {
            position: relative;
            margin-bottom: 2px;
          }

          /* Anneau tournant : bleu → or, la signature du logo */
          .ad-avatar-ring {
            position: absolute;
            inset: -8px;
            border-radius: 30px;
            background: conic-gradient(from 0deg, var(--pr), var(--or), var(--pr-lt), var(--pr));
            opacity: 0.25;
            animation: spin-ring 4s linear infinite;
          }

          .ad-avatar-ring-inner {
            position: absolute;
            inset: 2px;
            border-radius: 28px;
            background: var(--ad-bg);
          }

          @keyframes spin-ring {
            to { transform: rotate(360deg); }
          }

          .ad-avatar {
            width: 84px;
            height: 84px;
            border-radius: 24px;
            background: linear-gradient(135deg, var(--pr-dk), var(--pr));
            border: 2px solid var(--or);
            color: #fff;
            font-weight: 700;
            font-size: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 32px rgba(31, 58, 79, 0.30);
            position: relative;
            transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            cursor: default;
          }

          .ad-avatar:hover {
            transform: scale(1.06) rotate(-3deg);
            box-shadow: 0 12px 48px rgba(31, 58, 79, 0.45);
          }

          .ad-avatar-status {
            position: absolute;
            bottom: 2px;
            right: 2px;
            width: 16px;
            height: 16px;
            background: #10b981;
            border-radius: 50%;
            border: 3px solid white;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .ad-status-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #10b981;
            display: inline-block;
            animation: pulse-dot 2s ease-in-out infinite;
          }

          @keyframes pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.7); }
          }

          .ad-title-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
          }

          .ad-title-prefix {
            font-size: 14px;
            color: #94A3B8;
            font-weight: 500;
            letter-spacing: 0.06em;
            text-transform: uppercase;
          }

          /* Nom de l'établissement : même police que les titres de page (Cinzel) */
          .ad-title {
            margin: 0;
            font-family: 'Cinzel', 'Times New Roman', serif;
            font-size: 36px;
            font-weight: 700;
            color: var(--pr-dk);
            letter-spacing: 0.01em;
            text-align: center;
            line-height: 1.2;
          }

          .ad-badges {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: center;
            margin-top: 2px;
          }

          .ad-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 5px 16px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            letter-spacing: 0.02em;
            backdrop-filter: blur(10px);
            transition: all 0.3s ease;
          }

          .ad-badge:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          }

          /* Année scolaire = identité → or */
          .ad-badge-primary {
            background: rgba(176, 141, 42, 0.13);
            color: var(--or-dk);
          }

          /* En ligne = statut → vert */
          .ad-badge-success {
            background: rgba(16, 185, 129, 0.08);
            color: #059669;
          }

          .ad-badge-info {
            background: rgba(63, 100, 127, 0.10);
            color: var(--pr);
          }

          .ad-badge i {
            font-size: 14px;
          }

          .ad-stats {
            display: flex;
            gap: 32px;
            margin-top: 6px;
            padding: 12px 24px;
            background: rgba(255, 255, 255, 0.5);
            backdrop-filter: blur(20px);
            border-radius: 16px;
            border: 1px solid rgba(226, 232, 240, 0.5);
          }

          .ad-stat {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 4px 8px;
            border-radius: 8px;
            transition: all 0.3s ease;
          }

          .ad-stat:hover {
            background: rgba(63, 100, 127, 0.05);
          }

          .ad-stat-icon {
            width: 40px;
            height: 40px;
            border-radius: 12px;
            background: rgba(63, 100, 127, 0.08);
            color: var(--pr);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
          }

          .ad-stat-info {
            display: flex;
            flex-direction: column;
          }

          .ad-stat-value {
            font-size: 24px;
            font-weight: 800;
            color: var(--t);
            line-height: 1;
          }

          .ad-stat-label {
            font-size: 11px;
            color: #94A3B8;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin-top: 1px;
          }

          /* === QUOTE === */
          .ad-quote {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 6px;
            padding: 8px 20px 8px 16px;
            background: rgba(255, 255, 255, 0.4);
            backdrop-filter: blur(10px);
            border-radius: 12px;
            border: 1px solid rgba(226, 232, 240, 0.3);
            opacity: 0.8;
            transition: all 0.3s ease;
          }

          .ad-quote:hover {
            opacity: 1;
            transform: scale(1.02);
          }

          .ad-quote-icon {
            font-size: 24px;
            color: var(--or);
            opacity: 0.55;
          }

          .ad-quote-content {
            display: flex;
            flex-direction: column;
          }

          .ad-quote-text {
            font-size: 13px;
            color: #475569;
            font-weight: 500;
            font-style: italic;
          }

          .ad-quote-author {
            font-size: 11px;
            color: #94A3B8;
            font-weight: 600;
            margin-top: 1px;
          }

          /* === ANIMATIONS D'ENTRÉE === */
          .ad-hero-content > * {
            animation: fadeSlideUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
            opacity: 0;
          }

          .ad-header { animation-delay: 0.1s; }
          .ad-main { animation-delay: 0.3s; }

          @keyframes fadeSlideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          /* === RESPONSIVE === */
          @media (max-width: 992px) {
            .ad-stats { gap: 20px; padding: 12px 16px; }
            .ad-title { font-size: 30px; }
          }

          @media (max-width: 768px) {
            .ad-accueil { height: calc(100vh - 70px); }
            .ad-hero-content { padding: 24px 20px; max-height: 520px; }

            .ad-header {
              flex-direction: column;
              gap: 8px;
              align-items: flex-start;
              padding: 10px 16px;
            }

            .ad-header-right {
              width: 100%;
              justify-content: flex-start;
              flex-wrap: wrap;
            }

            .ad-dashboard-btn {
              font-size: 12px;
              padding: 6px 12px;
            }

            .ad-title { font-size: 26px; }
            .ad-avatar { width: 68px; height: 68px; font-size: 24px; border-radius: 20px; }
            .ad-avatar-ring { border-radius: 26px; inset: -6px; }
            .ad-stats { flex-wrap: wrap; justify-content: center; gap: 16px; }
            .ad-stat { flex: 1; min-width: 120px; }
            .ad-quote { display: none; }
            .ad-mouse-glow { display: none; }
            .ad-ornament-1, .ad-ornament-2 { display: none; }
          }

          @media (max-width: 480px) {
            .ad-hero-content { padding: 16px 12px; max-height: 460px; }
            .ad-title { font-size: 21px; }
            .ad-title-prefix { font-size: 12px; }
            .ad-avatar { width: 56px; height: 56px; font-size: 20px; border-radius: 16px; }
            .ad-avatar-ring { border-radius: 20px; inset: -4px; }
            .ad-avatar-status { width: 12px; height: 12px; border-width: 2px; }
            .ad-stats { padding: 10px 12px; gap: 12px; flex-direction: column; width: 100%; }
            .ad-stat { width: 100%; }
            .ad-stat-value { font-size: 20px; }
            .ad-badges { gap: 4px; }
            .ad-badge { font-size: 11px; padding: 4px 10px; }
            .ad-time { font-size: 12px; padding: 3px 10px; }
            .ad-date { font-size: 11px; }
            .ad-greeting { font-size: 13px; }
            .ad-ornament-3, .ad-ornament-4 { display: none; }
          }

          @media (max-height: 700px) {
            .ad-hero-content { max-height: 480px; padding: 20px 24px; }
            .ad-main { gap: 8px; padding: 6px 0; }
            .ad-avatar { width: 60px; height: 60px; font-size: 22px; }
            .ad-avatar-ring { inset: -4px; }
            .ad-title { font-size: 24px; }
            .ad-stats { padding: 8px 16px; gap: 12px; }
            .ad-stat-value { font-size: 18px; }
            .ad-quote { display: none; }
            .ad-badges { gap: 4px; }
          }
        `}</style>
      </div>
    </div>
  );
}